import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    TextField,
    InputAdornment,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    IconButton
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { PaymentProviderConfigViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import { useLoading, useAuth, useSnackbar, useNavigation } from "../../../Context/Contexts";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
type Props = {
    viewModel: PaymentProviderConfigViewModel | null;
    onSave: (vm: PaymentProviderConfigViewModel) => void;
};

const AdyenPaymentProviderWizard: React.FC<Props> = ({ viewModel, onSave }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [localViewModel, setLocalViewModel] = useState<PaymentProviderConfigViewModel>(viewModel ?? {});
    const { showSnackbar } = useSnackbar();
    const [showPassword, setShowPassword] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const handleChange = (field: keyof PaymentProviderConfigViewModel, value: string | null) => {
        if (!localViewModel) return;
        setLocalViewModel((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const validateStepAndNavigate = (newStep: number) => {
        if (newStep < 0) {
            setStep((s) => s + newStep);
            return;
        }
        if (step == 0 && !validateDescription()) return;
        if (step == 1 && !validateSettling()) return;
        // Add per-step validation if needed
        if (step == 2 && !validateRefund()) return;
        setStep((s) => s + 1);
    };

    const handleFinish = () => {
        if (!localViewModel) return;
        if (!validateSettling() && !validateRefund()) return;

        onSave(localViewModel);
    };

    const cancelWizard = () => {
        window.history.back();
    };

    const validateDescription = () => {
        if (!localViewModel.description || localViewModel.description.trim() === "") {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }
        return true;
    }
    const validateSettling = () => {
        const {
            paymentProviderInfo4,  // Merchant account
            paymentProviderInfo6,  // API key
            paymentProviderInfo8,  // URL
        } = localViewModel;

        const anyFilled =
            !!paymentProviderInfo4?.trim() ||
            !!paymentProviderInfo6?.trim() ||
            !!paymentProviderInfo8?.trim();

        const allFilled =
            !!paymentProviderInfo4?.trim() &&
            !!paymentProviderInfo6?.trim() &&
            !!paymentProviderInfo8?.trim();

        if (anyFilled && !allFilled) {
            showSnackbar(t("dialog.settlingIncomplete"), "warning");
            return false;
        }

        return true;
    };
    const validateRefund = () => {
        const {
            paymentProviderInfo11, // URL
            paymentProviderInfo12, // API username
            paymentProviderInfo13, // API password
        } = localViewModel;

        const anyFilled =
            !!paymentProviderInfo11?.trim() ||
            !!paymentProviderInfo12?.trim() ||
            !!paymentProviderInfo13?.trim();

        const allFilled =
            !!paymentProviderInfo11?.trim() &&
            !!paymentProviderInfo12?.trim() &&
            !!paymentProviderInfo13?.trim();

        if (anyFilled && !allFilled) {
            showSnackbar(t("dialog.refundIncomplete"), "warning");
            return false;
        }

        return true;
    };
    return (
        <Box sx={{ width: "100%" }}>
            <Stepper activeStep={step} sx={{ mb: 3, overflowX: "auto" }} alternativeLabel>
                <Step><StepLabel> {t("common.general")}</StepLabel></Step>
                <Step><StepLabel>Adyen – {t("common.settling")}</StepLabel></Step>
                <Step><StepLabel>Adyen – {t("common.refund")}</StepLabel></Step>
            </Stepper>
            {step === 0 && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.description")}
                            value={localViewModel.description || ""}
                            onChange={(e) => handleChange("description", e.target.value)}
                            fullWidth
                            margin="normal"
                            error={!localViewModel.description || localViewModel.description.trim() === ""}
                            helperText={
                                !localViewModel.description || localViewModel.description.trim() === ""
                                    ? t("help.descriptionRequired")
                                    : ""
                            }
                        />
                    </Grid>
                    </Grid>
            )}

            {step === 1 && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.apiKey")}
                            value={localViewModel.paymentProviderInfo6 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo6", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.apiKey")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.merchantAccount")}
                            value={localViewModel.paymentProviderInfo4 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo4", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.merchantAccount")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.settlementUrl")}
                            value={localViewModel.paymentProviderInfo8 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo8", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.settlementUrl")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>


                    <Grid item xs={12}>
                        <TextField
                            label={"Adyen Secret"}
                            value={localViewModel.paymentProviderInfo9 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo9", e.target.value)}
                            fullWidth
                            margin="normal"
                            type={showPassword ? "text" : "password"}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <InfoButton infoText={t("help.adyenSecret")} />
                                            </Box>
                                            <IconButton
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                edge="end"
                                                size="small"
                                                sx={{ padding: 0.5 }}
                                            >
                                                {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                                            </IconButton>
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={"Adyen URL Prefix"}
                            value={localViewModel.paymentProviderInfo5 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo5", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.adyenPrefix")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            {step === 2 && (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={`Adyen ${t("common.apiUsername")}`}
                            value={localViewModel.paymentProviderInfo12 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo12", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.apiUsername")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={`Adyen ${t("common.apiPassword")}`}
                            value={localViewModel.paymentProviderInfo13 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo13", e.target.value)}
                            fullWidth
                            margin="normal"
                            type={showPassword ? "text" : "password"}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <InfoButton infoText={t("help.apiPassword")} />
                                            </Box>
                                            <IconButton
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                edge="end"
                                                size="small"
                                                sx={{ padding: 0.5 }}
                                            >
                                                {showPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                                            </IconButton>
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={t("common.refundUrl")}
                            value={localViewModel.paymentProviderInfo11 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo11", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.refundUrl")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={t("common.merchantAccount")}
                            value={localViewModel.paymentProviderInfo4 || ""}
                            onChange={(e) => handleChange("paymentProviderInfo4", e.target.value)}
                            fullWidth
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={t("help.merchantAccount")} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                </Grid>
            )}

          


            <Box sx={{ marginTop: 2, textAlign: "center" }}>
                {step < 2 ? (
                    <Button variant="contained" onClick={() => validateStepAndNavigate(1)}>
                        {t("common.next")}
                    </Button>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleFinish}>
                        {t("common.finish")}
                    </Button>
                )}
                <Button disabled={step === 0} onClick={() => validateStepAndNavigate(-1)}>
                    {t("common.back")}
                </Button>
                <Button variant="contained" color="warning" onClick={cancelWizard}>
                    {t("common.cancelWizard")}
                </Button>
         
            </Box>
        </Box>
    );
};

export default AdyenPaymentProviderWizard;
